export const VARIABLE_TYPE_STRING = 'string';
export const VARIABLE_TYPE_NUMBER = 'number';
export const VARIABLE_TYPE_ARRAY = 'array';

/** True when the row is an explicit number variable. */
export const isNumberVariable = (row) => row?.type === VARIABLE_TYPE_NUMBER;

/**
 * Keep only a typed number: optional leading `-`, digits, one `.`.
 * Intermediate drafts (`-`, `.`, `-.`) stay so the field can be edited.
 */
export const sanitizeNumberInput = (raw) => {
  const text = String(raw ?? '');
  let out = '';
  let hasDot = false;
  for (const ch of text) {
    if (ch === '-' && out.length === 0) {
      out += ch;
      continue;
    }
    if (ch === '.' && !hasDot) {
      hasDot = true;
      out += ch;
      continue;
    }
    if (ch >= '0' && ch <= '9') out += ch;
  }
  return out;
};

/** Persist empty for blank / incomplete drafts (`-`, `.`, `-.`). */
export const normalizeNumberValue = (raw) => {
  const sanitized = sanitizeNumberInput(raw);
  if (!sanitized || sanitized === '-' || sanitized === '.' || sanitized === '-.') {
    return '';
  }
  return Number.isFinite(Number(sanitized)) ? sanitized : '';
};

/** True when the row is an array variable (explicit type or legacy `values`). */
export const isArrayVariable = (row) =>
  row?.type === VARIABLE_TYPE_ARRAY ||
  (row?.type !== VARIABLE_TYPE_STRING &&
    row?.type !== VARIABLE_TYPE_NUMBER &&
    Array.isArray(row?.values));

/** Normalize array slot list — always at least one (possibly empty) entry. */
export const normalizeArrayValues = (values) => {
  const list = Array.isArray(values) ? values.map((v) => String(v ?? '')) : [];
  return list.length > 0 ? list : [''];
};

/** Sentinel index: print every non-empty array slot, one per line (`name[*]`). */
export const ARRAY_INDEX_ALL = '*';

export const isArrayAllIndex = (index) =>
  index === ARRAY_INDEX_ALL || index === '*';

/**
 * Join array slots for `{{name[*]}}` — non-empty values, one per line.
 * (`printall` is accepted as a parse alias and normalized to `*`.)
 */
export const joinArrayValuesForPrint = (values) =>
  (Array.isArray(values) ? values : [])
    .map((v) => String(v ?? ''))
    .filter((v) => v.trim() !== '')
    .join('\n');

export const toArrayVariable = (row) => {
  if (!row || isArrayVariable(row)) {
    return row
      ? { ...row, type: VARIABLE_TYPE_ARRAY, values: normalizeArrayValues(row.values) }
      : row;
  }
  return {
    ...row,
    type: VARIABLE_TYPE_ARRAY,
    values: [String(row.value ?? '')],
    value: '',
  };
};

export const toStringVariable = (row) => {
  if (!row || !isArrayVariable(row)) {
    return row ? { ...row, type: VARIABLE_TYPE_STRING } : row;
  }
  const first = normalizeArrayValues(row.values)[0] ?? '';
  const next = { ...row, type: VARIABLE_TYPE_STRING, value: first };
  delete next.values;
  return next;
};
